import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { C } from '../../theme/palette';
import { ApplicationCard } from './ApplicationCard.jsx';
import { DoubleAuthCard } from './DoubleAuthCard.jsx';
import { BoutonInstaller } from '../../pwa/BoutonInstaller.jsx';
import { MesDonneesCard } from './MesDonneesCard.jsx';
import { MotDePasseCard, aUnMotDePasse } from './MotDePasseCard.jsx';
import { useSession } from '../../api/session.jsx';
import { useModeConnexion } from '../../api/modeConnexion';

export function SecuritePage({ onNav, soc, estMixte, univers, setUnivers }) {
  const { session } = useSession();
  const codeOuvrier = useModeConnexion()?.codeOuvrier === true;
  // L'ouvrier y trouve son mot de passe et son code de connexion (le numéro
  // qui le reçoit par SMS) ; les réglages d'accès de l'entreprise ne le
  // concernent pas.
  if (session?.role === 'ouvrier') {
    return (
      <Shell
        active="plus"
        onNav={onNav}
        soc={soc}
        back
        onBack={() => onNav('plus')}
        pageTitle="🔒 Sécurité"
        estMixte={estMixte}
        univers={univers}
        setUnivers={setUnivers}
      >
        {aUnMotDePasse(session) ? (
          <>
            <MotDePasseCard />
            <DoubleAuthCard />
          </>
        ) : (
          <Card style={{ marginBottom: 12 }}>
            <Bar icon="🔑" title="Ton accès" color={C.navy} />
            <div style={{ padding: '13px 15px', fontSize: 12, lineHeight: 1.65, color: C.ink }}>
              {codeOuvrier ? (
                <>
                  Tu entres avec ton <b>code à 4 chiffres</b> : tu n'as pas de mot de passe à
                  changer. Code oublié ou à remplacer ? Ton responsable le redéfinit dans « Équipe
                  ».
                </>
              ) : (
                <>
                  Le code à 4 chiffres n'ouvre plus SwissTech. Demande à ton responsable une
                  nouvelle invitation : tu choisiras ton adresse e-mail et un mot de passe, puis tu
                  recevras à chaque connexion un code à 6 chiffres.
                </>
              )}
            </div>
          </Card>
        )}
        <MesDonneesCard />
      </Shell>
    );
  }
  const Card2 = ({ icon, titre, children, col }) => (
    <Card style={{ marginBottom: 12 }}>
      <Bar icon={icon} title={titre} color={col || C.navy} />
      <div style={{ padding: '13px 15px', fontSize: 12, lineHeight: 1.65, color: C.ink }}>
        {children}
      </div>
    </Card>
  );
  const li = { display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 9 };
  const num = (n) => (
    <span
      style={{
        flex: '0 0 auto',
        width: 22,
        height: 22,
        borderRadius: 99,
        background: C.navy,
        color: '#fff',
        fontSize: 11,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {n}
    </span>
  );
  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      back
      onBack={() => onNav('plus')}
      pageTitle="🔒 Sécurité"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        style={{
          background: 'linear-gradient(135deg,' + C.navy + ',' + C.navyM + ')',
          color: '#fff',
          borderRadius: 14,
          padding: '16px 16px',
          marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 900 }}>🔒 Protège ton compte</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.75)', marginTop: 5 }}>
          SwissTech protège tes données. Suis ces étapes pour sécuriser ton accès au maximum.
        </div>
      </div>

      <Card2 icon="✅" titre="À faire pour sécuriser ton compte" col={C.green}>
        <div style={li}>
          {num(1)}
          <div>
            <b>Choisis un code / mot de passe fort.</b> Évite 0000, 1234 ou ta date de naissance.
          </div>
        </div>
        <div style={li}>
          {num(2)}
          <div>
            <b>Active la double authentification (2FA).</b> À chaque connexion, un 2ᵉ code t'est
            demandé : celui de ton application Authenticator, ou un code reçu par e-mail (cartes
            plus bas) — même si quelqu'un a ton mot de passe, il ne peut pas entrer.
          </div>
        </div>
        <div style={li}>
          {num(3)}
          <div>
            <b>Ne communique JAMAIS ton code</b> à personne (ni téléphone, ni message). Un vrai
            support ne te le demandera jamais.
          </div>
        </div>
        <div style={li}>
          {num(4)}
          <div>
            <b>Déconnecte-toi</b> sur un appareil partagé.
          </div>
        </div>
      </Card2>

      <Card2 icon="🔐" titre="La double authentification (2FA)" col={C.teal}>
        Une 2ᵉ sécurité à la connexion, après le mot de passe :
        <div style={{ marginTop: 10 }}>
          <div style={{ marginBottom: 8 }}>
            📧 <b>Par e-mail</b> — un code à 6 chiffres t'est envoyé par mail. <b>Disponible</b> :
            chaque utilisateur l'active pour son compte, dans la carte ci-dessous.
          </div>
          <div style={{ marginBottom: 8 }}>
            📱 <b>Par SMS</b> — un code à 6 chiffres sur ton téléphone. <i>À venir.</i>
          </div>
          <div style={{ marginBottom: 2 }}>
            🔑 <b>Par Authenticator</b> (Google Authenticator / Microsoft Authenticator) — tu
            scannes un QR une fois, puis un code qui <b>change toutes les 30 secondes</b>.{' '}
            <b>Disponible</b> : le plus sûr, dans la carte ci-dessous.
          </div>
        </div>
      </Card2>

      <Card2 icon="👤" titre="Espace patron & employés" col={C.amber}>
        <div style={{ marginBottom: 7 }}>
          • Chaque personne a <b>son propre accès</b> (un compte = une personne).
        </div>
        <div style={{ marginBottom: 7 }}>
          • Le patron <b>choisit ce que chaque rôle voit</b> (Réglages → Accès) et peut{' '}
          <b>masquer les bénéfices</b>.
        </div>
        <div style={{ marginBottom: 7 }}>
          • Le patron peut <b>révoquer un accès</b> en 1 clic (employé qui part).
        </div>
        <div>
          • Tes données sont <b>confidentielles</b> — ne les copie pas hors du logiciel.
        </div>
      </Card2>

      <Card2 icon="🛡️" titre="Comment SwissTech te protège" col={C.navy}>
        <div style={{ marginBottom: 7 }}>
          • <b>Isolation</b> : chaque entreprise ne voit QUE ses données.
        </div>
        <div style={{ marginBottom: 7 }}>
          • <b>Connexion chiffrée (HTTPS)</b> + mots de passe chiffrés.
        </div>
        <div style={{ marginBottom: 7 }}>
          • <b>Blocage automatique</b> après plusieurs mauvais essais (anti-robot).
        </div>
        <div>
          • <b>Sauvegardes automatiques</b> de tes données.
        </div>
      </Card2>

      {/* Les réglages propres à la production suivent les cartes de la maquette. */}
      <MotDePasseCard />
      <ApplicationCard />
      <DoubleAuthCard />
      <MesDonneesCard />
      <BoutonInstaller />

      <div
        style={{
          fontSize: 8.5,
          color: C.muted,
          textAlign: 'center',
          margin: '4px 0 6px',
          lineHeight: 1.5,
        }}
      >
        Aucune application n'est sûre à 100 %, mais ces mesures sont le standard des logiciels
        professionnels.
      </div>
    </Shell>
  );
}
